// Renders a custom surface inside a sandboxed iframe and bridges it to the
// plugin's worker through the host. The iframe is sandbox="allow-scripts" with
// NO allow-same-origin, an opaque origin, and (on desktop) the per-surface CSP
// served by plugins.rs. All messaging is host-mediated via SurfaceBridge; the
// surface never reaches the host DOM or the worker directly.

import { useEffect, useId, useRef, type ReactElement } from 'react';
import { SurfaceBridge, connectSurfaceFrame, snapshotThemeVars } from './surfaceBridge';
import type { PluginSystem } from '../setup';

export interface SurfaceHostProps {
  system: PluginSystem;
  pluginId: string;
  surfaceId: string;
  /** The iframe document URL (plugin://<id>/<path> on desktop). */
  src: string;
  title: string;
  className?: string;
}

export function SurfaceHost({
  system,
  pluginId,
  surfaceId,
  src,
  title,
  className,
}: SurfaceHostProps): ReactElement {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const reactId = useId();
  const instanceId = `${pluginId}:${surfaceId}:${reactId}`;

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || typeof window === 'undefined') return;
    const frameWindow = iframe.contentWindow;
    if (!frameWindow) return;

    const read = (name: string): string => {
      try {
        return window.getComputedStyle(document.documentElement).getPropertyValue(name);
      } catch {
        return '';
      }
    };

    const bridge = new SurfaceBridge({
      instanceId,
      theme: snapshotThemeVars(read),
      port: { post: (frame) => frameWindow.postMessage(frame, '*') },
      onMessageToWorker: (message) => {
        void system.host.deliverSurfaceMessage(pluginId, surfaceId, message).catch(() => undefined);
      },
    });

    const conn = connectSurfaceFrame({ hostWindow: window, frameWindow, bridge });

    const unsub = system.host.onSurfaceMessage((e) => {
      if (e.pluginId === pluginId && e.surfaceId === surfaceId) bridge.sendToFrame(e.message);
    });

    return () => {
      unsub();
      conn.dispose();
      bridge.dispose();
    };
  }, [system, pluginId, surfaceId, instanceId]);

  return (
    <iframe
      ref={iframeRef}
      className={className ? `mp-surface ${className}` : 'mp-surface'}
      title={title}
      src={src}
      // SECURITY: allow-scripts ONLY. NEVER add allow-same-origin.
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
    />
  );
}
